import { Form, InputNumber, Select, Modal, Space } from "antd";
import { ApiService } from "api/api.service";

import { useEffect } from "react";
import { TbFlagCheck, TbScale, TbTrophy } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
const { Option } = Select;
const StrategyConfigModal = ({ isOpen, appKey }: { isOpen: boolean; appKey?: string }) => {
  const navigate = useNavigate();
  const getUser = ApiService.user.useGetCurrentUser();
  const triggerPercentageLedger = ApiService.ledger.useCreatePercentageLedger();
  const getOrganisationDetails = ApiService.organisation.useGetBySlug(getUser.data?.organisation?.slug);
  const pandaDocConfig = getOrganisationDetails.data?.pandadoc_mappings ?? [];
  const [form] = Form.useForm();
  const programSplitType = Form.useWatch(["program_split", "variant"], form);
  const creditorSplitType = Form.useWatch(["creditor_split", "variant"], form);
  const addStrategyConfig = ApiService.application.useSetStrategyPercentage();
  const onSubmit = (values: any) => {
    addStrategyConfig.mutate(
      { values, appKey: appKey },
      {
        onSuccess: () => {
          triggerPercentageLedger.mutate(
            { appKey: appKey, weeks: 4 },
            {
              onSuccess: () => {
                navigate("/dashboard/application/" + appKey);
              },
            },
          );
        },
      },
    );
  };
  useEffect(() => {
    if (pandaDocConfig.length > 0) {
      form.setFieldsValue({
        template_id: pandaDocConfig[pandaDocConfig.length - 2]._id,
      });
    }
  }, [pandaDocConfig]);
  const formatter = (value: number | undefined) => {
    if (value === undefined) return "";
    const [start, end] = `${value}`.split(".") || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${end ? `${v}.${end}` : `${v}`}`;
  };
  const parser = (value: string | undefined) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number;
  const pctProps = {
    step: 0.01,
    min: 0,
    max: 1,
    formatter: (value: any) => (value ? `${(value * 100).toFixed(0)}%` : "0%"),
    parser: (value: any) => (value ? Number.parseFloat(value.replace("%", "")) / 100 : 0),
    className: "w-full",
  };
  return (
    <Modal
      onOk={form.submit}
      okButtonProps={{ loading: addStrategyConfig.isPending || triggerPercentageLedger.isPending }}
      centered
      open={isOpen}
      closeIcon={null}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          initial_bias_amount: 0,
          program_fee: 0,
          success_fee: 0,
          initial_fee: 0,
          program_split: {
            variant: "percentage",
            value: 0,
          },
          creditor_split: {
            variant: "percentage",
            value: 0,
          },
        }}
        onFinish={onSubmit}
      >
        <div className="grid grid-cols-2 gap-2">
          <Form.Item
            label={
              <Space>
                <TbScale /> Upfront Amount
              </Space>
            }
            name="initial_bias_amount"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} formatter={formatter} parser={parser} prefix={"$"} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Program Fee (%)" name="program_fee" rules={[{ required: true }]}>
            <InputNumber {...pctProps} style={{ width: "100%" }} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Form.Item
            label={
              <Space>
                <TbTrophy /> Success Fee (%)
              </Space>
            }
            name="success_fee"
            rules={[{ required: true }]}
          >
            <InputNumber {...pctProps} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label={
              <Space>
                <TbFlagCheck /> Initial Fee (%)
              </Space>
            }
            name="initial_fee"
            rules={[{ required: true }]}
          >
            <InputNumber {...pctProps} style={{ width: "100%" }} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Form.Item label="Program Split Variant" name={["program_split", "variant"]}>
            <Select
              onChange={() => {
                form.setFieldsValue({
                  program_split: {
                    value: undefined,
                  },
                });
              }}
            >
              <Option value="fixed">Fixed</Option>
              <Option value="percentage">Percentage</Option>
            </Select>
          </Form.Item>

          <Form.Item rules={[{ required: true }]} label="Program Split Value" name={["program_split", "value"]}>
            {programSplitType === "percentage" ? (
              <InputNumber {...pctProps} style={{ width: "100%" }} />
            ) : (
              <InputNumber prefix="$" formatter={formatter} parser={parser} min={0} style={{ width: "100%" }} />
            )}
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Form.Item label="Creditor Split Variant" name={["creditor_split", "variant"]}>
            <Select
              onChange={() => {
                form.setFieldsValue({
                  creditor_split: {
                    value: undefined,
                  },
                });
              }}
            >
              <Option value="fixed">Fixed</Option>
              <Option value="percentage">Percentage</Option>
            </Select>
          </Form.Item>

          <Form.Item rules={[{ required: true }]} label="Creditor Split Value" name={["creditor_split", "value"]}>
            {creditorSplitType === "percentage" ? (
              <InputNumber {...pctProps} style={{ width: "100%" }} />
            ) : (
              <InputNumber prefix="$" formatter={formatter} parser={parser} min={0} style={{ width: "100%" }} />
            )}
          </Form.Item>
        </div>
        <Form.Item label="Template" name={"template_id"} rules={[{ required: true }]}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select PandaDoc template for this strategy"
            options={pandaDocConfig.map((config) => ({
              label: config.title,
              value: config._id,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default StrategyConfigModal;
